/**
 * Feed Post Component - LeadFlow V2.0 Module 7
 * Displays a single post with interactions
 */

import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Avatar } from 'primereact/avatar';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { Image } from 'primereact/image';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { confirmDialog } from 'primereact/confirmdialog';
import PostComments from './PostComments';
import { socialFeedService } from '../../services/socialFeedService';

// Helper function to get user initials
const getUserInitials = (authorProfile) => {
    if (!authorProfile) return 'U';

    const fullName = authorProfile.full_name || authorProfile.name || '';

    if (fullName) {
        const names = fullName.trim().split(' ');
        if (names.length >= 2) {
            return `${names[0].charAt(0)}${names[names.length - 1].charAt(0)}`.toUpperCase();
        }
        return names[0].charAt(0).toUpperCase();
    }

    const firstName = authorProfile.first_name || '';
    const lastName = authorProfile.last_name || '';

    if (firstName && lastName) {
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    } else if (firstName) {
        return firstName.charAt(0).toUpperCase();
    }

    return 'U';
};

// Custom Repost Icon (Twitter/X style retweet icon)
const RepostIcon = ({ className = '', style = {}, filled = false }) => (
    <svg
        viewBox="0 0 24 24"
        width="1em"
        height="1em"
        fill="currentColor"
        className={className}
        style={{ verticalAlign: 'middle', ...style }}
    >
        <path d="M4.5 3.88l4.432 4.14-1.364 1.46L5.5 7.55V16c0 1.1.896 2 2 2H13v2H7.5c-2.209 0-4-1.79-4-4V7.55L1.432 9.48.068 8.02 4.5 3.88zM16.5 6H11V4h5.5c2.209 0 4 1.79 4 4v8.45l2.068-1.93 1.364 1.46-4.432 4.14-4.432-4.14 1.364-1.46 2.068 1.93V8c0-1.1-.896-2-2-2z" />
    </svg>
);

// Simple time formatter
const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    const intervals = {
        year: 31536000,
        month: 2592000,
        week: 604800,
        day: 86400,
        hour: 3600,
        minute: 60
    };

    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
        const interval = Math.floor(seconds / secondsInUnit);
        if (interval >= 1) {
            return `${interval} ${unit}${interval > 1 ? 's' : ''} ago`;
        }
    }
    return 'just now';
};

// Check if media item is a document (backend returns type: 'file' for documents)
const isDocumentMedia = (media) => {
    if (typeof media === 'object') {
        if (media.type === 'document' || media.type === 'file') return true;
        // Check mime_type if available
        const mime = (media.mime_type || '').toLowerCase();
        if (mime && !mime.startsWith('image/') && !mime.startsWith('video/')) return true;
    }
    // Check string URLs for document extensions
    if (typeof media === 'string') {
        const ext = media.split('.').pop()?.toLowerCase().split('?')[0];
        if (['pdf', 'csv', 'txt', 'xlsx', 'xls', 'doc', 'docx'].includes(ext)) return true;
    }
    return false;
};

// Get file extension from media object (handles name, url, and mime_type)
const getDocumentExt = (media) => {
    // Try to get extension from filename first (most reliable)
    const name = (media.name || '').toLowerCase();
    if (name) {
        const nameParts = name.split('.');
        if (nameParts.length > 1) {
            const ext = nameParts.pop().split('?')[0];
            if (ext && ext.length <= 5 && !ext.includes('/')) return ext;
        }
    }
    // Try URL - extract filename from path
    const url = (media.url || (typeof media === 'string' ? media : '')).toLowerCase();
    if (url) {
        const urlPath = url.split('?')[0]; // Remove query string
        const fileName = urlPath.split('/').pop(); // Get last path segment
        if (fileName) {
            const urlParts = fileName.split('.');
            if (urlParts.length > 1) {
                const ext = urlParts.pop();
                if (ext && ext.length <= 5 && !ext.includes('/')) return ext;
            }
        }
    }
    // Fallback: derive from mime_type
    const mime = (media.mime_type || '').toLowerCase();
    if (mime.includes('pdf')) return 'pdf';
    if (mime.includes('spreadsheet') || mime.includes('excel')) return 'xlsx';
    if (mime.includes('csv')) return 'csv';
    if (mime.includes('text')) return 'txt';
    return '';
};

// Get icon for document type based on file name or URL
const getDocumentTypeIcon = (media) => {
    const ext = getDocumentExt(media);
    if (ext === 'pdf') return 'pi pi-file-pdf';
    if (['xlsx', 'xls'].includes(ext)) return 'pi pi-file-excel';
    if (ext === 'csv') return 'pi pi-file-excel';
    if (ext === 'txt') return 'pi pi-file';
    return 'pi pi-file';
};

// Get document color based on type
const getDocumentColor = (media) => {
    const ext = getDocumentExt(media);
    if (ext === 'pdf') return '#E53935';
    if (['xlsx', 'xls', 'csv'].includes(ext)) return '#43A047';
    if (ext === 'txt') return '#1E88E5';
    return '#757575';
};

// Format file size for display
const formatDocSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// Check if URL is a YouTube video
const isYouTubeUrl = (url) => {
    return url && (url.includes('youtube.com') || url.includes('youtu.be'));
};

// Check if URL is a Vimeo video
const isVimeoUrl = (url) => {
    return url && url.includes('vimeo.com');
};

// Extract YouTube video ID from URL
const getYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
};

// Extract Vimeo video ID from URL
const getVimeoId = (url) => {
    const regExp = /vimeo.com\/(\d+)/;
    const match = url.match(regExp);
    return match ? match[1] : null;
};

// Get embed URL for video
const getVideoEmbedUrl = (url) => {
    if (isYouTubeUrl(url)) {
        const videoId = getYouTubeId(url);
        return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    } else if (isVimeoUrl(url)) {
        const videoId = getVimeoId(url);
        return videoId ? `https://player.vimeo.com/video/${videoId}` : null;
    }
    return null;
};

const FeedPost = ({ post, onUpdate, onDelete, isSavedPostsView = false, isMyPostsView = false, onUnsave, repostedBy = null }) => {
    const navigate = useNavigate();
    const toast = useRef(null);
    const menu = useRef(null);
    const shareMenu = useRef(null);
    const [showComments, setShowComments] = useState(false);

    // Check if mobile
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    // Get current user to check if they can delete/edit the post
    let currentUser = {};
    try {
        currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    } catch (e) {
        console.error('Error parsing user from localStorage:', e);
    }

    // Use loose equality (==) to handle string/number type differences
    // post.user_id might be number from API, currentUser.id might be string from localStorage
    const isOwnPost = isMyPostsView ||
                      (post.user_id && currentUser.id && String(post.user_id) === String(currentUser.id)) ||
                      (post.author_profile?.user_id && currentUser.id && String(post.author_profile.user_id) === String(currentUser.id));
    const isAdmin = currentUser.user_type === 'admin';
    const canDelete = isOwnPost || isAdmin;
    const canEdit = isOwnPost; // Only owner can edit visibility
    const [liked, setLiked] = useState(post.is_liked || false);
    const [saved, setSaved] = useState(post.is_saved || false);
    const [reposted, setReposted] = useState(post.is_reshared || false);
    const [likeCount, setLikeCount] = useState(post.like_count || 0);
    const [commentCount, setCommentCount] = useState(post.comment_count || 0);
    const [shareCount, setShareCount] = useState(post.share_count || 0);
    const [loading, setLoading] = useState(false);
    const [reposting, setReposting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [liking, setLiking] = useState(false); // Prevent rapid like clicks
    const [profilePhotoError, setProfilePhotoError] = useState(false); // Track profile photo load errors

    // Repost dialog state
    const [showRepostDialog, setShowRepostDialog] = useState(false);
    const [repostQuote, setRepostQuote] = useState('');

    // Edit dialog state
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [editedVisibility, setEditedVisibility] = useState(post.visibility || 'public');

    // Navigate to profile view
    const handleProfileClick = () => {
        if (post.author_profile?.id) {
            navigate(`/app/profile/${post.author_profile.id}`);
        }
    };

    // Handler for comment added
    const handleCommentAdded = () => {
        setCommentCount(prev => prev + 1);
    };

    // Handler for comment deleted
    const handleCommentDeleted = () => {
        setCommentCount(prev => Math.max(0, prev - 1));
    };

    // Get post type display info
    const getPostTypeInfo = () => {
        const types = {
            general: { label: 'General', icon: 'pi-comment', severity: 'info' },
            lead_share: { label: 'Lead Share', icon: 'pi-briefcase', severity: 'success' },
            achievement: { label: 'Achievement', icon: 'pi-trophy', severity: 'warning' },
            article: { label: 'Article', icon: 'pi-file', severity: 'help' },
            question: { label: 'Question', icon: 'pi-question-circle', severity: 'danger' }
        };
        return types[post.post_type] || types.general;
    };

    const typeInfo = getPostTypeInfo();

    // Handle like toggle with optimistic UI and debouncing
    const handleLike = async () => {
        // Prevent rapid clicks
        if (liking) return;

        const wasLiked = liked;
        try {
            setLiking(true);
            // Optimistic UI update - update immediately for instant feedback
            setLiked(!wasLiked);
            setLikeCount(prev => wasLiked ? Math.max(0, prev - 1) : prev + 1);

            // Make API call
            const response = wasLiked
                ? await socialFeedService.removeReaction(post.id, 'like')
                : await socialFeedService.addReaction(post.id, 'like');

            if (!response.success) {
                // Revert on failure
                setLiked(wasLiked);
                setLikeCount(prev => wasLiked ? prev + 1 : Math.max(0, prev - 1));
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.message || 'Failed to update like',
                    life: 3000
                });
            }
        } catch (error) {
            console.error('Error toggling like:', error);

            // Revert optimistic update on error
            setLiked(wasLiked);
            setLikeCount(prev => wasLiked ? prev + 1 : Math.max(0, prev - 1));

            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to update like',
                life: 3000
            });
        } finally {
            setLiking(false);
        }
    };

    // Handle unsave - for saved posts view
    const handleUnsave = async () => {
        confirmDialog({
            message: 'Remove this post from your saved items?',
            header: 'Unsave Post',
            icon: 'pi pi-info-circle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    setSaving(true);
                    const response = await socialFeedService.toggleSave(post.id, false);
                    if (response.success) {
                        setSaved(false);
                        toast.current?.show({
                            severity: 'info',
                            summary: 'Unsaved',
                            detail: 'Post removed from saved items',
                            life: 2000
                        });
                        // Notify parent to remove from list if in saved posts view
                        if (onUnsave) {
                            onUnsave(post.id);
                        }
                    }
                } catch (error) {
                    console.error('Error unsaving post:', error);
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to unsave post',
                        life: 3000
                    });
                } finally {
                    setSaving(false);
                }
            }
        });
    };

    // Handle save toggle - prevent duplicate saves
    const handleSave = async () => {
        // Check if already saved
        if (saved) {
            handleUnsave();
            return;
        }

        // Prevent duplicate saves
        if (saving) {
            toast.current?.show({
                severity: 'info',
                summary: 'Saving',
                detail: 'Please wait...',
                life: 2000
            });
            return;
        }

        try {
            setSaving(true);
            const response = await socialFeedService.toggleSave(post.id, true);
            if (response.success) {
                setSaved(true);
                toast.current?.show({
                    severity: 'success',
                    summary: 'Saved',
                    detail: 'Post saved to your collection',
                    life: 2000
                });
            }
        } catch (error) {
            console.error('Error saving post:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to save post',
                life: 3000
            });
        } finally {
            setSaving(false);
        }
    };

    // Handle repost - opens dialog for optional quote
    const handleRepostClick = () => {
        // Check if already reposted
        if (reposted) {
            // Show undo repost confirmation
            confirmDialog({
                message: 'Do you want to undo your repost?',
                header: 'Undo Repost',
                icon: 'pi pi-replay',
                acceptClassName: 'p-button-danger',
                accept: handleUndoRepost
            });
            return;
        }

        // Open repost dialog
        setShowRepostDialog(true);
    };

    // Handle repost submission
    const handleRepost = async () => {
        if (reposting) return;

        try {
            setReposting(true);
            const response = await socialFeedService.repostPost(post.id, repostQuote || null);
            if (response.success) {
                setReposted(true);
                setShareCount(prev => prev + 1);
                setShowRepostDialog(false);
                setRepostQuote('');
                toast.current?.show({
                    severity: 'success',
                    summary: 'Reposted',
                    detail: 'Post shared to your feed',
                    life: 3000
                });
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.message || 'Failed to repost',
                    life: 3000
                });
            }
        } catch (error) {
            console.error('Error reposting:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to repost',
                life: 3000
            });
        } finally {
            setReposting(false);
        }
    };

    // Handle undo repost
    const handleUndoRepost = async () => {
        try {
            setReposting(true);
            const response = await socialFeedService.undoRepost(post.id);
            if (response.success) {
                setReposted(false);
                setShareCount(prev => Math.max(0, prev - 1));
                toast.current?.show({
                    severity: 'info',
                    summary: 'Repost Removed',
                    detail: 'Your repost has been removed',
                    life: 3000
                });
            }
        } catch (error) {
            console.error('Error undoing repost:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to undo repost',
                life: 3000
            });
        } finally {
            setReposting(false);
        }
    };

    // Handle external share (copy link)
    const handleCopyLink = async () => {
        const postUrl = `${window.location.origin}/app/leadflow/feed?post=${post.id}`;

        try {
            // Try modern clipboard API first
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(postUrl);
                toast.current?.show({
                    severity: 'success',
                    summary: 'Link Copied',
                    detail: 'Post link copied to clipboard',
                    life: 2000
                });
            } else {
                // Fallback for older browsers
                const textArea = document.createElement('textarea');
                textArea.value = postUrl;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                textArea.style.top = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();

                try {
                    const successful = document.execCommand('copy');
                    document.body.removeChild(textArea);

                    if (successful) {
                        toast.current?.show({
                            severity: 'success',
                            summary: 'Link Copied',
                            detail: 'Post link copied to clipboard',
                            life: 2000
                        });
                    } else {
                        throw new Error('Copy command failed');
                    }
                } catch (err) {
                    document.body.removeChild(textArea);
                    throw err;
                }
            }
        } catch (error) {
            console.error('Copy failed:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Copy Failed',
                detail: 'Unable to copy link. Please try again.',
                life: 3000
            });
        }
    };

    // Share menu items for external sharing
    const shareMenuItems = [
        {
            label: 'Copy Link',
            icon: 'pi pi-link',
            command: handleCopyLink
        }
    ];

    // Handle edit visibility
    const handleEditVisibility = async () => {
        try {
            const response = await socialFeedService.updatePost(post.id, {
                visibility: editedVisibility
            });

            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Updated',
                    detail: 'Post visibility updated successfully',
                    life: 2000
                });
                setShowEditDialog(false);
                // Update post in parent if callback exists
                if (onUpdate) {
                    onUpdate({ ...post, visibility: editedVisibility });
                }
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.message || 'Failed to update post',
                    life: 3000
                });
            }
        } catch (error) {
            console.error('Error updating post:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to update post',
                life: 3000
            });
        }
    };

    // Handle delete
    const handleDelete = () => {
        confirmDialog({
            message: 'Are you sure you want to delete this post? This action cannot be undone.',
            header: 'Delete Post',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                setLoading(true);
                try {
                    const response = await socialFeedService.deletePost(post.id);
                    if (response.success) {
                        toast.current?.show({
                            severity: 'success',
                            summary: 'Deleted',
                            detail: 'Post deleted successfully',
                            life: 2000
                        });
                        if (onDelete) {
                            onDelete(post.id);
                        }
                    } else {
                        // Check if it's an authorization error
                        const isAuthError = response.message?.includes('only delete your own') ||
                                          response.message?.includes('not authorized');

                        toast.current?.show({
                            severity: isAuthError ? 'warn' : 'error',
                            summary: isAuthError ? 'Not Allowed' : 'Error',
                            detail: response.message || 'Failed to delete post',
                            life: 4000
                        });
                    }
                } catch (error) {
                    console.error('Error deleting post:', error);

                    // Check if error response contains authorization message
                    const errorMessage = error.response?.data?.message || 'Failed to delete post';
                    const isAuthError = errorMessage.includes('only delete your own') ||
                                      errorMessage.includes('not authorized');

                    toast.current?.show({
                        severity: isAuthError ? 'warn' : 'error',
                        summary: isAuthError ? 'Not Allowed' : 'Error',
                        detail: errorMessage,
                        life: 4000
                    });
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    // Menu items - different for saved posts view vs normal feed
    // Edit Visibility: Only for user's own posts
    // Delete: For user's own posts OR admin users (admin can delete any post)
    const menuItems = isSavedPostsView
        ? [
            // Edit Visibility - only for post owner
            ...(isOwnPost ? [{
                label: 'Edit Visibility',
                icon: 'pi pi-eye',
                command: () => {
                    setEditedVisibility(post.visibility || 'public');
                    setShowEditDialog(true);
                }
            }] : []),
            // Unsave - always available in saved view
            {
                label: 'Unsave Post',
                icon: 'pi pi-bookmark-fill',
                command: handleUnsave
            },
            // Delete - for own posts OR admin
            ...(canDelete ? [{
                label: isAdmin && !isOwnPost ? 'Delete Post (Admin)' : 'Delete Post',
                icon: 'pi pi-trash',
                command: handleDelete,
                className: 'text-red-500'
            }] : [])
        ]
        : [
            // Edit Visibility - only for post owner
            ...(isOwnPost ? [{
                label: 'Edit Visibility',
                icon: 'pi pi-eye',
                command: () => {
                    setEditedVisibility(post.visibility || 'public');
                    setShowEditDialog(true);
                }
            }] : []),
            // Save/Unsave - always available
            {
                label: saved ? 'Unsave Post' : 'Save Post',
                icon: `pi pi-bookmark${saved ? '-fill' : ''}`,
                command: handleSave
            },
            // Delete - for own posts OR admin
            ...(canDelete ? [{
                label: isAdmin && !isOwnPost ? 'Delete Post (Admin)' : 'Delete Post',
                icon: 'pi pi-trash',
                command: handleDelete,
                className: 'text-red-500'
            }] : [])
        ];


    // Format hashtags in content and preserve line breaks
    const formatContent = (text) => {
        if (!text) return '';
        // Escape HTML entities first to prevent XSS, then apply safe transformations only
        const escaped = text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#x27;');
        return escaped
            .replace(/\n/g, '<br>')
            .replace(/#(\w+)/g, '<span class="text-primary font-semibold">#$1</span>');
    };

    // Repost dialog footer
    const repostDialogFooter = (
        <div className="flex justify-content-end gap-2">
            <Button
                label="Cancel"
                icon="pi pi-times"
                className="p-button-text"
                onClick={() => {
                    setShowRepostDialog(false);
                    setRepostQuote('');
                }}
            />
            <Button
                onClick={handleRepost}
                loading={reposting}
                className="flex align-items-center gap-2"
            >
                <RepostIcon />
                <span>Repost</span>
            </Button>
        </div>
    );

    return (
        <>
            <Toast ref={toast} />

            {/* Repost Dialog */}
            <Dialog
                header="Repost to your feed"
                visible={showRepostDialog}
                style={{ width: isMobile ? '95vw' : '500px', maxWidth: '500px' }}
                onHide={() => {
                    setShowRepostDialog(false);
                    setRepostQuote('');
                }}
                footer={repostDialogFooter}
                modal
                className="p-fluid"
            >
                <div className="mb-3">
                    <label className="block text-600 mb-2">Add your thoughts (optional)</label>
                    <InputTextarea
                        value={repostQuote}
                        onChange={(e) => setRepostQuote(e.target.value)}
                        rows={3}
                        placeholder="Say something about this post..."
                        autoResize
                        className="w-full"
                        maxLength={500}
                    />
                    <small className="text-500 block text-right mt-1">
                        {repostQuote.length}/500
                    </small>
                </div>

                {/* Preview of original post */}
                <div className="surface-100 border-round p-3">
                    <div className="flex gap-2 align-items-center mb-2">
                        {post.author_profile?.profile_photo_url_full ? (
                            <div
                                className="border-circle overflow-hidden"
                                style={{ width: '32px', height: '32px' }}
                            >
                                <img
                                    src={post.author_profile.profile_photo_url_full}
                                    alt={post.author_profile.full_name || 'Profile'}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            </div>
                        ) : (
                            <Avatar
                                label={getUserInitials(post.author_profile)}
                                shape="circle"
                                style={{
                                    backgroundColor: '#6366F1',
                                    color: '#ffffff',
                                    width: '32px',
                                    height: '32px',
                                    fontSize: '0.875rem'
                                }}
                            />
                        )}
                        <div>
                            <div className="font-semibold text-900 text-sm">
                                {post.author_profile?.full_name || 'Anonymous User'}
                            </div>
                            <div className="text-500 text-xs">
                                {formatTimeAgo(post.created_at)}
                            </div>
                        </div>
                    </div>
                    <p className="text-700 text-sm m-0 line-height-3" style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical'
                    }}>
                        {post.content}
                    </p>
                </div>
            </Dialog>

            <Card className="feed-post mb-2 md:mb-3">
                {/* Reposted By Indicator */}
                {repostedBy && (
                    <div className="flex align-items-center gap-2 text-600 text-xs md:text-sm mb-2 md:mb-3 pb-2 border-bottom-1 surface-border">
                        <RepostIcon />
                        <span>
                            <span className="font-semibold cursor-pointer hover:text-primary" onClick={() => navigate(`/app/profile/${repostedBy.id}`)}>
                                {repostedBy.full_name}
                            </span>
                            {' '}reposted
                        </span>
                    </div>
                )}

                {/* Post Header */}
                <div className="flex justify-content-between align-items-start mb-2 md:mb-3">
                    <div className="flex gap-2 md:gap-3 flex-1 min-w-0">
                        {post.author_profile?.profile_photo_url_full && !profilePhotoError ? (
                            <Avatar
                                image={post.author_profile.profile_photo_url_full}
                                size={isMobile ? "normal" : "large"}
                                shape="circle"
                                className="flex-shrink-0 cursor-pointer"
                                style={{ width: isMobile ? '40px' : '48px', height: isMobile ? '40px' : '48px' }}
                                onClick={handleProfileClick}
                                title={`View ${post.author_profile.full_name}'s profile`}
                                onImageError={() => {
                                    // On image error, switch to initials avatar
                                    setProfilePhotoError(true);
                                }}
                            />
                        ) : (
                            <Avatar
                                label={getUserInitials(post.author_profile)}
                                size={isMobile ? "normal" : "large"}
                                shape="circle"
                                className="flex-shrink-0 cursor-pointer"
                                style={{
                                    backgroundColor: '#6366F1',
                                    color: '#ffffff',
                                    width: isMobile ? '40px' : '48px',
                                    height: isMobile ? '40px' : '48px',
                                    fontSize: isMobile ? '1rem' : '1.25rem'
                                }}
                                onClick={handleProfileClick}
                                title={`View ${post.author_profile?.full_name || 'user'}'s profile`}
                            />
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="flex align-items-center gap-2 flex-wrap">
                                <span
                                    className="font-bold text-900 cursor-pointer hover:text-primary text-sm md:text-base white-space-nowrap overflow-hidden text-overflow-ellipsis"
                                    onClick={handleProfileClick}
                                    title={`View ${post.author_profile?.full_name || 'user'}'s profile`}
                                    style={{ maxWidth: isMobile ? '120px' : 'none' }}
                                >
                                    {post.author_profile?.full_name || 'Anonymous User'}
                                </span>
                                <Tag
                                    icon={`pi ${typeInfo.icon}`}
                                    value={typeInfo.label}
                                    severity={typeInfo.severity}
                                    className="text-xs"
                                    style={{ fontSize: isMobile ? '0.65rem' : '0.75rem', padding: isMobile ? '0.15rem 0.4rem' : '0.25rem 0.5rem' }}
                                />
                            </div>
                            <div className="text-600 text-xs md:text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ maxWidth: isMobile ? '180px' : 'none' }}>
                                {post.author_profile?.headline || 'Financial Professional'}
                            </div>
                            <div className="text-500 text-xs mt-1">
                                {formatTimeAgo(post.created_at)}
                                {post.edited_at && ' (edited)'}
                            </div>
                        </div>
                    </div>

                    <div className="flex-shrink-0">
                        <Menu model={menuItems} popup ref={menu} />
                        <Button
                            icon="pi pi-ellipsis-v"
                            className="p-button-text p-button-rounded p-button-sm"
                            onClick={(e) => menu.current?.toggle(e)}
                        />
                    </div>
                </div>

                {/* Post Content */}
                <div
                    className="text-900 mb-2 md:mb-3 line-height-3 text-sm md:text-base"
                    style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
                    dangerouslySetInnerHTML={{ __html: formatContent(post.content) }}
                />

                {/* Post Media (Images/Videos) - Use media_urls_full for proper domain handling */}
                {(() => {
                    const allMediaUrls = post.media_urls_full || post.media_urls;
                    if (!allMediaUrls || allMediaUrls.length === 0) return null;

                    // Filter out documents - they are rendered in the Document Attachments section below
                    const mediaUrls = allMediaUrls.filter(m => !isDocumentMedia(m));
                    if (mediaUrls.length === 0) return null;

                    return (
                        <div className="mb-2 md:mb-3">
                            {mediaUrls.length === 1 ? (
                                // Single media item - check individual item type
                                <div className="border-round overflow-hidden">
                                    {(() => {
                                        const mediaItem = mediaUrls[0];
                                        const mediaUrl = mediaItem.url || mediaItem;
                                        const isVideo = (typeof mediaItem === 'object' && mediaItem.type === 'video') || post.media_type === 'video';

                                        if (isVideo) {
                                            const embedUrl = getVideoEmbedUrl(mediaUrl);

                                            if (embedUrl) {
                                                // YouTube or Vimeo embed
                                                return (
                                                    <div className="relative" style={{ paddingBottom: '56.25%', height: 0 }}>
                                                        <iframe
                                                            src={embedUrl}
                                                            title="Video player"
                                                            frameBorder="0"
                                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                            allowFullScreen
                                                            className="absolute top-0 left-0 w-full h-full border-round"
                                                        />
                                                    </div>
                                                );
                                            } else {
                                                // Direct video file
                                                return (
                                                    <video
                                                        src={mediaUrl}
                                                        controls
                                                        className="w-full border-round"
                                                        style={{ maxHeight: isMobile ? '250px' : '400px', objectFit: 'contain', backgroundColor: '#000' }}
                                                    />
                                                );
                                            }
                                        } else {
                                            // Image
                                            return (
                                                <Image
                                                    src={mediaUrl}
                                                    alt="Post media"
                                                    className="w-full"
                                                    imageClassName="w-full border-round"
                                                    imageStyle={{ maxHeight: isMobile ? '250px' : '400px', objectFit: 'cover' }}
                                                    preview
                                                />
                                            );
                                        }
                                    })()}
                                </div>
                            ) : (
                                // Multiple media items - grid layout
                                <div className="grid">
                                    {mediaUrls.slice(0, 4).map((media, index) => {
                                        const mediaUrl = typeof media === 'string' ? media : (media.url || media.media_url);
                                        // Check individual item type first, then fall back to post media_type
                                        const isVideo = (typeof media === 'object' && media.type === 'video') ||
                                                       (post.media_type === 'video' && typeof media === 'string');

                                        return (
                                            <div
                                                key={index}
                                                className={`${mediaUrls.length === 2 ? 'col-6' : mediaUrls.length === 3 ? (index === 0 ? 'col-12' : 'col-6') : 'col-6'} p-1`}
                                            >
                                                <div className="relative border-round overflow-hidden h-full">
                                                    {isVideo ? (
                                                        (() => {
                                                            const embedUrl = getVideoEmbedUrl(mediaUrl);
                                                            if (embedUrl) {
                                                                // YouTube or Vimeo embed
                                                                return (
                                                                    <div className="relative" style={{ paddingBottom: '56.25%', height: 0, minHeight: isMobile ? '120px' : '150px' }}>
                                                                        <iframe
                                                                            src={embedUrl}
                                                                            title={`Video player ${index + 1}`}
                                                                            frameBorder="0"
                                                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                                            allowFullScreen
                                                                            className="absolute top-0 left-0 w-full h-full border-round"
                                                                        />
                                                                    </div>
                                                                );
                                                            } else {
                                                                // Direct video file
                                                                return (
                                                                    <video
                                                                        src={mediaUrl}
                                                                        controls
                                                                        className="w-full h-full border-round"
                                                                        style={{ minHeight: isMobile ? '100px' : '150px', maxHeight: isMobile ? '150px' : '200px', objectFit: 'cover', backgroundColor: '#000' }}
                                                                    />
                                                                );
                                                            }
                                                        })()
                                                    ) : (
                                                        <Image
                                                            src={mediaUrl}
                                                            alt={`Post media ${index + 1}`}
                                                            className="w-full"
                                                            imageClassName="w-full border-round"
                                                            imageStyle={{ height: isMobile ? '120px' : '200px', objectFit: 'cover' }}
                                                            preview
                                                        />
                                                    )}
                                                    {/* Show overlay for remaining images if more than 4 */}
                                                    {index === 3 && mediaUrls.length > 4 && (
                                                        <div
                                                            className="absolute top-0 left-0 w-full h-full flex align-items-center justify-content-center"
                                                            style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
                                                        >
                                                            <span className="text-white text-xl md:text-2xl font-bold">
                                                                +{mediaUrls.length - 4}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Document Attachments */}
                {(() => {
                    const mediaUrls = post.media_urls_full || post.media_urls;
                    if (!mediaUrls || mediaUrls.length === 0) return null;

                    const documents = mediaUrls.filter(m => isDocumentMedia(m));
                    if (documents.length === 0) return null;

                    return (
                        <div className="mb-2 md:mb-3 p-2 md:p-3 surface-50 border-round border-1 surface-border">
                            <div className="flex align-items-center gap-2 mb-2">
                                <i className="pi pi-paperclip text-600 text-sm"></i>
                                <span className="text-xs md:text-sm font-medium text-600">
                                    {documents.length} {documents.length === 1 ? 'Attachment' : 'Attachments'}
                                </span>
                            </div>
                            <div className="flex flex-column gap-2">
                                {documents.map((doc, index) => {
                                    const docUrl = doc.url || doc;
                                    const rawName = doc.name || (typeof docUrl === 'string' ? docUrl.split('/').pop()?.split('?')[0] : '') || 'Document';
                                    const docName = rawName;
                                    const docSize = doc.size ? formatDocSize(doc.size) : '';
                                    const docExt = getDocumentExt(doc).toUpperCase() || 'FILE';

                                    return (
                                        <a
                                            key={index}
                                            href={docUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="no-underline"
                                            download
                                        >
                                            <div className="flex align-items-center gap-2 md:gap-3 p-2 surface-card border-round border-1 surface-border cursor-pointer hover:surface-hover transition-colors transition-duration-150">
                                                <div
                                                    className="flex align-items-center justify-content-center border-round flex-shrink-0"
                                                    style={{
                                                        width: isMobile ? '36px' : '40px',
                                                        height: isMobile ? '36px' : '40px',
                                                        backgroundColor: `${getDocumentColor(doc)}15`
                                                    }}
                                                >
                                                    <i className={getDocumentTypeIcon(doc)} style={{
                                                        color: getDocumentColor(doc),
                                                        fontSize: isMobile ? '1rem' : '1.25rem'
                                                    }}></i>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm md:text-base font-medium text-900" style={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {docName}
                                                    </div>
                                                    <div className="flex align-items-center gap-1 mt-1">
                                                        <span className="text-xs text-500">{docExt}</span>
                                                        {docSize && (
                                                            <>
                                                                <span className="text-xs text-500">&middot;</span>
                                                                <span className="text-xs text-500">{docSize}</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                <i className="pi pi-download text-500 text-sm md:text-base flex-shrink-0"></i>
                                            </div>
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })()}

                {/* Post Tags */}
                {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 md:gap-2 mb-2 md:mb-3">
                        {post.tags.map((tag, index) => (
                            <Tag
                                key={index}
                                value={`#${tag}`}
                                className="cursor-pointer text-xs"
                                severity="info"
                            />
                        ))}
                    </div>
                )}

                {/* LinkedIn-style Action Bar - Combined counts with actions */}
                <div
                    className="flex align-items-center justify-content-between pt-2 border-top-1 surface-border"
                    style={{ gap: '0.25rem', overflow: 'hidden' }}
                >
                    {/* Like Button with Count */}
                    <Button
                        className={`p-button-text p-button-sm flex align-items-center gap-1 ${liked ? 'text-red-500' : 'text-600'}`}
                        onClick={handleLike}
                        loading={liking}
                        disabled={liking}
                        tooltip={liked ? 'Unlike' : 'Like'}
                        tooltipOptions={{ position: 'top' }}
                        style={{ flex: '1 1 0', minWidth: 0, maxWidth: '20%', padding: isMobile ? '0.4rem 0.2rem' : '0.5rem' }}
                    >
                        <i className={`pi pi-heart${liked ? '-fill' : ''}`} style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}></i>
                        {likeCount > 0 && <span style={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}>{likeCount}</span>}
                    </Button>

                    {/* Comment Button with Count */}
                    <Button
                        className="p-button-text p-button-sm flex align-items-center gap-1 text-600"
                        onClick={() => setShowComments(!showComments)}
                        tooltip="Comment"
                        tooltipOptions={{ position: 'top' }}
                        style={{ flex: '1 1 0', minWidth: 0, maxWidth: '20%', padding: isMobile ? '0.4rem 0.2rem' : '0.5rem' }}
                    >
                        <i className="pi pi-comment" style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}></i>
                        {commentCount > 0 && <span style={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}>{commentCount}</span>}
                    </Button>

                    {/* Repost Button with Count */}
                    <Button
                        className={`p-button-text p-button-sm flex align-items-center gap-1 ${reposted ? 'text-green-500' : 'text-600'}`}
                        onClick={handleRepostClick}
                        loading={reposting}
                        tooltip={reposted ? 'Undo Repost' : 'Repost'}
                        tooltipOptions={{ position: 'top' }}
                        style={{ flex: '1 1 0', minWidth: 0, maxWidth: '20%', padding: isMobile ? '0.4rem 0.2rem' : '0.5rem' }}
                    >
                        <RepostIcon style={{ fontSize: isMobile ? '0.9rem' : '1rem' }} />
                        {shareCount > 0 && <span style={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}>{shareCount}</span>}
                    </Button>

                    {/* Share Button */}
                    <Menu model={shareMenuItems} popup ref={shareMenu} />
                    <Button
                        icon="pi pi-share-alt"
                        className="p-button-text p-button-sm text-600"
                        onClick={(e) => shareMenu.current?.toggle(e)}
                        tooltip="Share"
                        tooltipOptions={{ position: 'top' }}
                        style={{ flex: '1 1 0', minWidth: 0, maxWidth: '20%', padding: isMobile ? '0.4rem 0.2rem' : '0.5rem' }}
                    />

                    {/* Save/Bookmark Button */}
                    {isSavedPostsView ? (
                        <Button
                            icon="pi pi-bookmark-fill"
                            className="p-button-text p-button-sm text-blue-500"
                            onClick={handleUnsave}
                            loading={saving}
                            tooltip="Unsave"
                            tooltipOptions={{ position: 'top' }}
                            style={{ flex: '1 1 0', minWidth: 0, maxWidth: '20%', padding: isMobile ? '0.4rem 0.2rem' : '0.5rem' }}
                        />
                    ) : (
                        <Button
                            icon={`pi pi-bookmark${saved ? '-fill' : ''}`}
                            className={`p-button-text p-button-sm ${saved ? 'text-blue-500' : 'text-600'}`}
                            onClick={handleSave}
                            loading={saving}
                            tooltip={saved ? 'Saved' : 'Save'}
                            tooltipOptions={{ position: 'top' }}
                            style={{ flex: '1 1 0', minWidth: 0, maxWidth: '20%', padding: isMobile ? '0.4rem 0.2rem' : '0.5rem' }}
                        />
                    )}
                </div>

                {/* Comments Section */}
                {showComments && (
                    <>
                        <Divider />
                        <PostComments
                            postId={post.id}
                            onCommentAdded={handleCommentAdded}
                            onCommentDeleted={handleCommentDeleted}
                        />
                    </>
                )}
            </Card>

            {/* Edit Visibility Dialog */}
            <Dialog
                header="Edit Post Visibility"
                visible={showEditDialog}
                style={{ width: isMobile ? '95vw' : '450px', maxWidth: '450px' }}
                onHide={() => setShowEditDialog(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={() => setShowEditDialog(false)}
                        />
                        <Button
                            label="Update"
                            icon="pi pi-check"
                            onClick={handleEditVisibility}
                        />
                    </div>
                }
                modal
            >
                <div className="field">
                    <label htmlFor="visibility" className="block mb-2 font-medium">
                        <i className="pi pi-eye mr-2"></i>
                        Who can see this post?
                    </label>
                    <Dropdown
                        id="visibility"
                        value={editedVisibility}
                        options={[
                            { label: 'Public - Anyone can see', value: 'public', icon: 'pi pi-globe' },
                            { label: 'Connections - Only my connections', value: 'connections', icon: 'pi pi-users' },
                            { label: 'Tenant Only - Only my organization', value: 'tenant', icon: 'pi pi-building' }
                        ]}
                        onChange={(e) => setEditedVisibility(e.value)}
                        optionLabel="label"
                        className="w-full"
                        itemTemplate={(option) => (
                            <div className="flex align-items-center gap-2">
                                <i className={option.icon}></i>
                                <span>{option.label}</span>
                            </div>
                        )}
                        valueTemplate={(option) => option ? (
                            <div className="flex align-items-center gap-2">
                                <i className={option.icon}></i>
                                <span>{option.label}</span>
                            </div>
                        ) : <span>Select visibility</span>}
                    />
                    <small className="text-500 block mt-2">
                        You can change who can see this post at any time.
                    </small>
                </div>
            </Dialog>
        </>
    );
};

export default FeedPost;
